import { toValue, type MaybeRefOrGetter } from 'vue'
import type { AppError } from '@core/api/normalizeError'
import { orderKeys } from '../orderQueryKeys'
import { getOrderDocumentFile } from '../orderEndpoints'
import type { OrderDocument } from '../../types/orderTypes'
import { useLazyOrderFileQuery } from './useLazyOrderFileQuery'

interface UseOrderDocumentFileQueryOptions {
  /** The document to fetch — `null` while it has not been issued. */
  document: MaybeRefOrGetter<OrderDocument | null>
  /** Used when the document carries no file name of its own. */
  fallbackFileName: MaybeRefOrGetter<string>
  /** Rejection message when `load()` runs before the document exists. */
  missingMessage: string
}

/** An order document's file, fetched on demand and cached by its CDN url. */
export function useOrderDocumentFileQuery(options: UseOrderDocumentFileQueryOptions) {
  return useLazyOrderFileQuery(
    () => orderKeys.documentFile(toValue(options.document)?.url ?? ''),
    () => {
      const document = toValue(options.document)
      if (!document) {
        const failure: AppError = { message: options.missingMessage }
        return Promise.reject(failure)
      }
      return getOrderDocumentFile(document, toValue(options.fallbackFileName))
    },
  )
}
